import { formatDistanceToNowStrict } from 'date-fns';
import { useTranslation } from 'react-i18next';

import Iconify from 'src/components/Iconify';
import getDateLocale from 'src/utils/getDateLocale';
import { cn } from 'src/@/lib/utils';

const PLATFORM_ICON = {
  facebook: 'ic:baseline-facebook',
  instagram: 'mdi:instagram'
};

const PLATFORM_BADGE_BG = {
  facebook: 'bg-[#1877F2]',
  instagram: 'bg-gradient-to-tr from-amber-400 via-pink-500 to-violet-600'
};

// Item da sidebar no estilo Meta Business Suite: avatar do post com mini-badge da
// plataforma, primeira linha = publicação, segunda linha = "Autor - comentário".
export default function CommentPostItem({ post, isActive, onSelect }) {
  const { t } = useTranslation('chat');

  let relativeDate = '';
  try {
    if (post.last_comment_at) {
      relativeDate = formatDistanceToNowStrict(new Date(post.last_comment_at), { locale: getDateLocale() });
    }
  } catch (error) {
    relativeDate = '';
  }

  const lastComment = post.last_comment;
  const lastAuthor = lastComment
    ? (lastComment.isMe ? t('comments.you') : (lastComment.from?.name || lastComment.from?.username || ''))
    : '';

  return (
    <div
      onClick={() => onSelect(post)}
      className={cn(
        'group relative flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors',
        isActive ? 'bg-violet-50 dark:bg-violet-500/10' : 'hover:bg-gray-50 dark:hover:bg-neutral-800/60'
      )}
    >
      {/* Avatar do post com badge da plataforma (como o badge de canal nos chats) */}
      <div className="relative flex-shrink-0">
        <div className="w-11 h-11 rounded-full overflow-hidden bg-gray-100 dark:bg-neutral-800 flex items-center justify-center">
          {post.thumbnail_url ? (
            <img src={post.thumbnail_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <Iconify icon="solar:gallery-bold" width={18} className="text-gray-400 dark:text-neutral-500" />
          )}
        </div>
        <span className={cn(
          'absolute -bottom-0.5 -right-0.5 w-[18px] h-[18px] rounded-full flex items-center justify-center border-2 border-white dark:border-neutral-900',
          PLATFORM_BADGE_BG[post.platform]
        )}>
          <Iconify icon={PLATFORM_ICON[post.platform]} width={11} className="text-white" />
        </span>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[13px] font-medium text-gray-900 dark:text-white truncate">
            {post.message || t('comments.viewPost')}
          </span>
          {relativeDate && (
            <span className="text-[10px] text-gray-400 dark:text-neutral-500 flex-shrink-0">{relativeDate}</span>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 mt-0.5">
          {lastComment ? (
            <span className="text-[12px] text-gray-500 dark:text-neutral-400 truncate">
              <span className="font-medium">{lastAuthor}</span>
              {lastComment.text ? ` - ${lastComment.text}` : ''}
            </span>
          ) : (
            <span className="text-[12px] text-gray-400 dark:text-neutral-500 italic truncate">
              {t('comments.emptyComments')}
            </span>
          )}
          {post.unreplied > 0 && (
            <span className="min-w-[16px] h-[16px] flex items-center justify-center rounded-full bg-violet-500 text-white text-[9px] font-bold px-1 flex-shrink-0">
              {post.unreplied}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
